import React, { useState, useEffect } from 'react';
import {
  Modal,
  Button,
  Form,
  SelectPicker,
  Input,
  useToaster,
  Message,
} from 'rsuite';
import { DECK_FORMAT_RULES } from '../../helpers/deckRules';
import authFetch from '../../helpers/authFetch';

const VISIBILITY_OPTIONS = [
  { label: 'Private', value: 'PRIVATE' },
  { label: 'Invite Only', value: 'INVITE_ONLY' },
  { label: 'Public', value: 'PUBLIC' },
];

const SPACING = {
  formGroupMarginTop: 16,
};

const EditDeckModal = ({ open, onClose, collection, onUpdated }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    deck_type: null,
    visibility: 'PRIVATE',
  });
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const toaster = useToaster();

  useEffect(() => {
    if (open && collection) {
      setFormData({
        name: collection.name || '',
        description: collection.description || '',
        deck_type: collection.deck_type || null,
        visibility: collection.visibility || 'PRIVATE',
      });
      setError('');
    }
  }, [open, collection]);

  const handleClose = () => {
    setError('');
    onClose();
  };

  const setField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      setError('Deck name is required');
      return;
    }

    setSubmitting(true);
    setError('');

    const controller = new AbortController();

    try {
      const updatePayload = {
        name: formData.name,
        description: formData.description,
        deck_type: formData.deck_type,
        visibility: formData.visibility,
      };

      const response = await authFetch(`/collections/${collection.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatePayload),
        signal: controller.signal,
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to update deck');
      }

      toaster.push(
        <Message showIcon type="success" closable>
          Deck updated successfully
        </Message>,
        { placement: 'topCenter', duration: 3000 }
      );

      if (onUpdated) onUpdated();
      handleClose();
    } catch (err) {
      if (err.name !== 'AbortError') {
        setError(err.message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const deckTypeOptions = Object.entries(DECK_FORMAT_RULES).map(([key, rules]) => ({
    label: rules.name,
    value: key,
  }));

  return (
    <Modal size="sm" open={open} onClose={handleClose} backdrop="static">
      <Modal.Header>
        <Modal.Title>Edit Deck Details</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && (
          <Message showIcon type="error" closable onClose={() => setError('')} style={{ marginBottom: 16 }}>
            {error}
          </Message>
        )}
        <Form>
          <Form.Group controlId="name">
            <Form.ControlLabel>Deck Name</Form.ControlLabel>
            <Input
              value={formData.name}
              onChange={(val) => setField('name', val)}
              placeholder="Enter deck name"
              disabled={submitting}
            />
          </Form.Group>

          <Form.Group controlId="description" style={{ marginTop: SPACING.formGroupMarginTop }}>
            <Form.ControlLabel>Description</Form.ControlLabel>
            <Input
              as="textarea"
              rows={3}
              value={formData.description}
              onChange={(val) => setField('description', val)}
              placeholder="Enter deck description (optional)"
              disabled={submitting}
            />
          </Form.Group>

          <Form.Group controlId="deck_type" style={{ marginTop: SPACING.formGroupMarginTop }}>
            <Form.ControlLabel>Format</Form.ControlLabel>
            <SelectPicker
              data={deckTypeOptions}
              value={formData.deck_type}
              onChange={(val) => setField('deck_type', val)}
              placeholder="Select format"
              disabled={submitting}
              cleanable
            />
          </Form.Group>

          <Form.Group controlId="visibility" style={{ marginTop: SPACING.formGroupMarginTop }}>
            <Form.ControlLabel>Visibility</Form.ControlLabel>
            <SelectPicker
              data={VISIBILITY_OPTIONS}
              value={formData.visibility}
              onChange={(val) => setField('visibility', val)}
              disabled={submitting}
            />
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={handleClose} appearance="subtle" disabled={submitting}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} appearance="primary" loading={submitting}>
          Save Changes
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default EditDeckModal;
